import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Container, Spinner, Row, Col, Dropdown, Button, Offcanvas } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { FaSearch, FaChevronRight, FaRegDotCircle, FaInfoCircle, FaDownload, FaCalendarPlus, FaGoogle, FaEnvelopeOpenText, FaFilter } from "react-icons/fa";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Components
import Header from "../components/Header";
import Footer from "../components/Footer";
import API_BASE_URL from "../config";
import logo from "../assets/logo.png";

const AdmissionSchedule = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [showDrawer, setShowDrawer] = useState(false); // Mobile Drawer State

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/fetch/admissionDatesData`);
      if (response.data?.[0]?.admissionCalendar) {
        setData(response.data[0].admissionCalendar);
      }
    } catch (err) {
      console.error("Unable to sync academic calendar.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredData = useMemo(() => 
    data.filter(item => item.programName.toLowerCase().includes(searchTerm.toLowerCase())),
  [searchTerm, data]);

  const current = filteredData[activeIdx] || null;

  // --- CALENDAR LOGIC ---
  const formatCalendarDate = (dateStr) => {
    // Basic parser for "January 20, 2026" format
    const date = new Date(dateStr);
    return isNaN(date) ? new Date().toISOString().replace(/-|:|\.\d+/g, "") : date.toISOString().replace(/-|:|\.\d+/g, "");
  };

  const handleGoogleCalendar = (label, date, program) => {
    const start = formatCalendarDate(date);
    const url = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(program + ": " + label)}&dates=${start}/${start}&details=Official+Admission+Deadline&sf=true&output=xml`;
    window.open(url, "_blank");
  };

  const handleICSExport = (label, date, program) => {
    const start = formatCalendarDate(date);
    const icsContent = `BEGIN:VCALENDAR\nVERSION:2.0\nBEGIN:VEVENT\nSUMMARY:${program}: ${label}\nDTSTART:${start}\nDTEND:${start}\nDESCRIPTION:Official Deadline\nEND:VEVENT\nEND:VCALENDAR`;
    const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute("download", `${label.replace(/\s+/g, "_")}.ics`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // --- PDF EXPORT ---
  const downloadPDF = () => {
    if (!current) return;
    const doc = new jsPDF();
    
    // 1. Add University Branding (Letterhead)
    try {
      doc.addImage(logo, 'PNG', 14, 10, 25, 25);
    } catch (e) {
      console.warn("Logo not found for PDF", e);
    }
    
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(0, 43, 90); // Deep Navy
    doc.text("SUKKUR IBA UNIVERSITY", 45, 20);
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text("Merit - Quality - Excellence", 45, 26);
    doc.text("Official Admission Schedule", 45, 31);
    
    // Header Line
    doc.setDrawColor(0, 85, 164); // Primary Blue
    doc.setLineWidth(0.5);
    doc.line(14, 40, 196, 40);

    // 2. Program Title
    doc.setFontSize(16);
    doc.setTextColor(0, 43, 90);
    doc.text(`${current.programName} - Session ${current.year}`, 14, 52);
    
    let startY = 60;
    
    // 3. Phases & Tables
    current.phases.forEach((phase, index) => {
      // Check for page overflow
      if (startY > 250) {
        doc.addPage();
        startY = 20;
      }

      autoTable(doc, {
        startY: startY,
        head: [[{ content: phase.phaseName, colSpan: 2, styles: { halign: 'left', fillColor: [0, 85, 164] } }]],
        body: phase.dates.map(d => [d.label, d.date]),
        theme: 'striped',
        headStyles: { fontSize: 12, fontStyle: 'bold' },
        bodyStyles: { fontSize: 10, textColor: [50, 50, 50] },
        alternateRowStyles: { fillColor: [245, 248, 252] },
        margin: { left: 14, right: 14 },
      });
      
      startY = doc.lastAutoTable.finalY + 10;
      
      // Add Phase Notes if any
      if (phase.notes && phase.notes.length > 0) {
        doc.setFontSize(9);
        doc.setTextColor(120);
        doc.setFont("helvetica", "italic");
        phase.notes.forEach(note => {
          if (startY > 270) { doc.addPage(); startY = 20; }
          doc.text(`* ${note}`, 14, startY);
          startY += 5;
        });
        startY += 5;
        doc.setFont("helvetica", "normal");
      }
    });

    // 4. Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(`Generated on ${new Date().toLocaleDateString()} | Sukkur IBA University Admissions Portal`, 14, 285);
      doc.text(`Page ${i} of ${pageCount}`, 196, 285, { align: "right" });
    }

    doc.save(`${current.programName}_Official_Schedule.pdf`);
  };

  if (loading) return (
    <div className="premium-loader">
      <Spinner animation="border" variant="primary" />
      <p className="mt-3 fw-bold letter-spacing-2">SYNCING REPOSITORY</p>
    </div>
  );

  return (
    <div className="portal-master-wrapper">
      <Header />

      <section className="portal-sub-nav py-4 shadow-sm bg-white sticky-top" style={{ top: '0', zIndex: 1020 }}>
        <Container>
          <Row className="align-items-center">
            <Col md={5}>
              <h1 className="h4 fw-900 text-navy m-0">Admission <span className="text-primary">Timeline</span></h1>
            </Col>
            <Col md={7}>
              <div className="d-flex gap-3 justify-content-end align-items-center mt-3 mt-md-0">
                <div className="search-pill-container flex-grow-1">
                  <FaSearch className="search-icon" />
                  <input type="text" placeholder="Search tracks..." onChange={(e) => { setSearchTerm(e.target.value); setActiveIdx(0); }} />
                </div>
                {/* Mobile Track Selection */}
                <Button 
                    variant="primary" 
                    className="rounded-pill px-4 d-lg-none d-flex align-items-center gap-2 nav-hint-pulse shadow-sm h-100"
                    onClick={() => setShowDrawer(true)}
                >
                    <FaFilter /> <span className="fw-bold small">Select Track</span>
                </Button>
                {current && (
                  <Button variant="outline-primary" className="rounded-pill px-4 d-none d-lg-flex align-items-center" onClick={downloadPDF}>
                    <FaDownload className="me-2" /> PDF
                  </Button>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="portal-viewport bg-light-soft py-5">
        <Container className="main-portal-container section-standard-spacing position-relative" style={{ zIndex: 10 }}>
          <Row className="g-4">
            {/* Desktop Sidebar */}
            <Col lg={4} className="d-none d-lg-block">
              <div className="sticky-sidebar">
                <div className="sidebar-card border-0 shadow-sm">
                  <div className="card-label">ACADEMIC TRACKS</div>
                  <div className="program-list custom-scrollbar">
                    {filteredData.map((prog, i) => (
                      <button 
                        key={i} 
                        className={`prog-item w-100 d-flex align-items-center justify-content-between p-3 border-0 bg-transparent transition-all ${activeIdx === i ? 'is-active' : ''}`} 
                        onClick={() => setActiveIdx(i)}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <div className={`icon-circle-mini ${activeIdx === i ? 'active' : ''}`}>
                            <FaCalendarPlus />
                          </div>
                          <div className="text-start">
                              <div className="name fw-bold mb-0">{prog.programName}</div>
                              <div className="stats-tag">Session {prog.year}</div>
                          </div>
                        </div>
                        <FaChevronRight className={`chevron-mini ${activeIdx === i ? 'visible' : ''}`} />
                      </button>
                    ))}
                    {filteredData.length === 0 && (
                      <div className="p-4 text-center text-muted small">No tracks found.</div>
                    )}
                  </div>
                </div>
              </div>
            </Col>

            <Col lg={8}>
              <AnimatePresence mode="wait">
                {current ? (
                  <motion.div key={current.programName} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} className="details-card bg-white p-4 p-lg-5 rounded-4 shadow-sm h-100">
                    <div className="section-intro mb-5">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="category-tag">Official Release</div>
                        <Button variant="link" className="text-primary d-lg-none" onClick={downloadPDF}><FaDownload /> PDF</Button>
                      </div>
                      <h2 className="portal-heading text-navy display-5 mb-3">{current.programName}</h2>
                      <div className="ivy-divider mb-4" />
                      <p className="editorial-text-body text-muted mb-0">Detailed admission schedule for the Academic Year {current.year}. Please adhere to all deadlines.</p>
                    </div>

                    {current.phases.map((phase, pIdx) => (
                      <div key={pIdx} className="phase-block bg-white p-4 p-lg-5 rounded-4 shadow-sm mb-4 border-top border-primary border-4">
                        <h3 className="h5 fw-800 text-primary mb-4 text-uppercase letter-spacing-1">{phase.phaseName}</h3>
                        
                        <div className="timeline-v-stack">
                          {phase.dates.map((dateObj, dIdx) => (
                            <div key={dIdx} className="milestone-row d-flex flex-column flex-md-row justify-content-between align-items-md-center py-3 border-bottom-dashed gap-3">
                              <div className="d-flex align-items-center">
                                <div className="dot-indicator me-3 flex-shrink-0" />
                                <span className="fw-700 text-navy text-wrap">{dateObj.label}</span>
                              </div>
                              <div className="d-flex align-items-center justify-content-between justify-content-md-end gap-3 w-100 w-md-auto">
                                <span className="date-display flex-shrink-0">{dateObj.date}</span>
                                
                                <Dropdown align="end">
                                  <Dropdown.Toggle variant="light" size="sm" className="btn-icon rounded-circle flex-shrink-0">
                                    <FaCalendarPlus />
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu className="shadow-lg border-0">
                                    <Dropdown.Item onClick={() => handleGoogleCalendar(dateObj.label, dateObj.date, current.programName)}>
                                      <FaGoogle className="me-2 text-danger" /> Google Calendar
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleICSExport(dateObj.label, dateObj.date, current.programName)}>
                                      <FaEnvelopeOpenText className="me-2 text-primary" /> Outlook / iCal
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            </div>
                          ))}
                        </div>

                        {phase.notes.length > 0 && (
                          <div className="notes-box mt-4 p-3 rounded-3 bg-light">
                            {phase.notes.map((note, nIdx) => (
                              <p key={nIdx} className="small text-muted mb-1 m-0"><FaRegDotCircle className="me-2 text-warning" /> {note}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </motion.div>
                ) : (
                  <div className="text-center py-5">
                    <FaInfoCircle size={40} className="text-muted mb-3" />
                    <p>No admission tracks found matching your search.</p>
                  </div>
                )}
              </AnimatePresence>
            </Col>
          </Row>
        </Container>
      </section>
      
      {/* --- MOBILE PROGRAM DRAWER --- */}
      <Offcanvas show={showDrawer} onHide={() => setShowDrawer(false)} placement="bottom" className="h-75 rounded-top-4">
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title className="fw-bold text-navy">Select Program</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0 bg-light">
          <div className="program-list-mobile">
              {filteredData.map((prog, i) => (
                <button 
                key={i} 
                className={`prog-item mobile-item w-100 d-flex align-items-center justify-content-between p-3 border-0 bg-transparent transition-all ${activeIdx === i ? 'is-active' : ''}`} 
                onClick={() => { setActiveIdx(i); setShowDrawer(false); }}
                >
                <div className="d-flex align-items-center gap-3">
                    <div className={`icon-circle-mini ${activeIdx === i ? 'active' : ''}`}>
                         <FaCalendarPlus />
                    </div>
                    <div className="text-start">
                        <div className="name fw-bold mb-0 text-dark">{prog.programName}</div>
                        <div className="stats-tag">Session {prog.year}</div>
                    </div>
                </div>
                {activeIdx === i && <FaChevronRight className="chevron-mini visible" />}
                </button>
              ))}
           </div>
        </Offcanvas.Body>
      </Offcanvas>

      <Footer />

      <style>{`
        .portal-master-wrapper { background-color: #fff; min-height: 100vh; font-family: 'Inter', sans-serif; }
        .bg-light-soft { background-color: #f8faff; }
        .fw-900 { font-weight: 900; }
        .text-navy { color: #002b5a; }
        .ls-1 { letter-spacing: 1px; }

        /* Typography Sync */
        .category-tag { color: #0055a4; font-weight: 800; text-transform: uppercase; font-size: 0.8rem; letter-spacing: 2px; }
        .portal-heading { font-weight: 900; letter-spacing: -1.5px; line-height: 1.1; }
        .ivy-divider { width: 60px; height: 5px; background: #0055a4; border-radius: 10px; }
        .editorial-text-body { font-size: 1.1rem; line-height: 1.6; color: #475569; }

        .main-portal-container { margin-top: 0; padding-bottom: 100px; position: relative; z-index: 10; }
        
        .search-pill-container {
          background: #f1f4f9; border-radius: 50px; padding: 10px 25px;
          display: flex; align-items: center; transition: 0.3s;
        }
        .search-pill-container:focus-within { background: #fff; box-shadow: 0 10px 25px rgba(0,0,0,0.05); border: 1px solid #0055a4; }
        .search-pill-container input { border: none; background: transparent; outline: none; width: 100%; margin-left: 12px; font-weight: 500; color: #002b5a; }
        .search-icon { color: #94a3b8; }

        /* Sidebar Styling */
        .sticky-sidebar { position: sticky; top: 120px; z-index: 100; transition: top 0.3s; }
        .sidebar-card { background: #fff; border-radius: 24px; overflow: hidden; display: flex; flex-direction: column; max-height: 75vh; border: 1px solid #e2e8f0 !important; }
        
        .card-label { 
          padding: 18px 25px; background: #002b5a; color: white; 
          font-weight: 800; font-size: 0.75rem; letter-spacing: 2px; text-transform: uppercase;
        }

        .program-list { overflow-y: auto; flex: 1; }
        .custom-scrollbar::-webkit-scrollbar { width: 5px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }

        .prog-item {
          transition: 0.3s;
          border-bottom: 1px solid #f1f5f9 !important;
        }
        .prog-item:hover { background-color: #f8fafc !important; }
        .prog-item.is-active {
          background-color: #002b5a !important;
          border-left: 6px solid #ffc300 !important;
        }
        .prog-item.is-active .name { color: white !important; }
        .prog-item.is-active .stats-tag { color: rgba(255,255,255,0.7) !important; }
        .prog-item.is-active .icon-circle-mini { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.2); color: white; }

        .icon-circle-mini {
          width: 42px; height: 42px; background: #f8fafc; border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-size: 1.1rem; color: #64748b; transition: 0.3s;
          border: 1px solid #e2e8f0;
        }
        .icon-circle-mini.active { background: #002b5a; color: #fff; border-color: #002b5a; }

        .stats-tag { font-size: 0.75rem; color: #94a3b8; font-weight: 600; }
        .chevron-mini { font-size: 0.8rem; color: #cbd5e0; opacity: 0; transition: 0.3s; transform: translateX(-10px); }
        .chevron-mini.visible { opacity: 1; transform: translateX(0); color: #0055a4; }
        .prog-item.is-active .chevron-mini.visible { color: #ffc300; }

        .details-card { border-radius: 24px !important; border: 1px solid #e2e8f0 !important; }

        .phase-block { border-top: 4px solid #0055a4 !important; }
        .milestone-row { border-bottom: 1px dashed #e2e8f0; transition: 0.2s; }
        .milestone-row:last-child { border-bottom: none; }
        .milestone-row:hover { background: #f8faff; padding-left: 10px; padding-right: 10px; border-radius: 12px; }
        
        .dot-indicator { width: 8px; height: 8px; border-radius: 50%; background: #cbd5e0; transition: 0.3s; }
        .milestone-row:hover .dot-indicator { background: #0055a4; transform: scale(1.5); }

        .date-display {
          background: #f0f4f8; color: #0055a4; padding: 6px 16px;
          border-radius: 50px; font-weight: 800; font-size: 0.8rem;
        }

        .notes-box { border-left: 4px solid #ffc300; }

        @media (max-width: 991px) {
          .main-portal-container { margin-top: 0px; }
          .section-standard-spacing { padding-bottom: 40px; }
          .portal-heading { font-size: 1.8rem; word-break: break-word; }
          .details-card { padding: 1.5rem !important; }
          .phase-block { padding: 1.5rem !important; }
          .milestone-row { padding-left: 0 !important; padding-right: 0 !important; }
          .date-display { font-size: 0.75rem; padding: 4px 12px; }
          
          .program-list-mobile .prog-item.mobile-item { border-bottom: 1px solid #f1f5f9 !important; padding: 18px; }
          .program-list-mobile .prog-item.mobile-item.is-active { 
            background: #002b5a !important; 
            color: white !important; 
            border-left: 5px solid #ffc300 !important; 
          }
          .program-list-mobile .prog-item.mobile-item.is-active .name { color: white !important; }
          .program-list-mobile .prog-item.mobile-item.is-active .stats-tag { color: rgba(255,255,255,0.7) !important; }
          .program-list-mobile .prog-item.mobile-item.is-active .icon-circle-mini { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.2); color: white; }
          .sticky-sidebar { position: relative !important; top: 0 !important; }
        }

        /* Navigation Hint Pulse */
        @keyframes hintPulse {
          0% { box-shadow: 0 0 0 0 rgba(0, 85, 164, 0.4); }
          70% { box-shadow: 0 0 0 10px rgba(0, 85, 164, 0); }
          100% { box-shadow: 0 0 0 0 rgba(0, 85, 164, 0); }
        }
        .nav-hint-pulse {
          animation: hintPulse 2s infinite;
          border: 2px solid #fff !important;
        }
      `}</style>
    </div>
  );
};

export default AdmissionSchedule;
